import type { PostMultiplacerDrofusInterfaceProjectChangeEventsApiResponse } from "../../openapi";
export type ChangeRow =
  PostMultiplacerDrofusInterfaceProjectChangeEventsApiResponse["items"][number];
export type PresentedChange = ChangeRow & {
  roomChanges?: ChangeRow[];
  moveFrom?: string;
  moveTo?: string;
};
const pathOf = (row: ChangeRow) =>
  row.normalizedPath ?? row.fieldId ?? row.changeKey.replace(/^normalized:/, "");

// Only combine room fields for the same occurrence and snapshot pair on this page.
// A changed room ID is required: renaming a room alone is not evidence of a move.
export function presentChanges(rows: ChangeRow[]): PresentedChange[] {
  const groups = new Map<string, ChangeRow[]>();
  const key = (row: ChangeRow) =>
    JSON.stringify([
      row.projectNo,
      row.entityType,
      row.entityId,
      row.stableKey,
      row.baseRunId,
      row.runId,
    ]);
  for (const row of rows) {
    if (
      row.entityType !== "occurrence" ||
      row.changeType !== "updated" ||
      !pathOf(row).startsWith("room.")
    )
      continue;
    const list = groups.get(key(row)) ?? [];
    list.push(row);
    groups.set(key(row), list);
  }
  const emitted = new Set<string>();
  return rows.flatMap((row): PresentedChange[] => {
    const list = groups.get(key(row));
    const id = list?.find((item) => pathOf(item) === "room.id");
    if (
      !list ||
      list.length < 2 ||
      !list.includes(row) ||
      id?.oldValue == null ||
      id.newValue == null ||
      id.oldValue === id.newValue
    )
      return [row];
    const groupKey = key(row);
    if (emitted.has(groupKey)) return [];
    emitted.add(groupKey);
    const number = list.find((item) => pathOf(item) === "room.functionNo");
    return [
      {
        ...row,
        roomChanges: list,
        moveFrom: String(number?.oldValue ?? `Rom-ID ${id.oldValue}`),
        moveTo: String(number?.newValue ?? `Rom-ID ${id.newValue}`),
        affectedDisciplines: [...new Set(list.flatMap((item) => item.affectedDisciplines))],
      },
    ];
  });
}

export function valuePreview(row: PresentedChange, field: "oldValue" | "newValue"): string {
  if (row.roomChanges) return (field === "oldValue" ? row.moveFrom : row.moveTo) ?? "—";
  const value = row[field];
  if (value == null || value === "") return pathOf(row) === "occurrence.mmiCode" ? "Ikke angitt" : "—";
  if (row.changeType !== "updated") {
    const context = row.context;
    return [
      context.article?.name ?? "Forekomst",
      context.article?.compCode,
      context.room?.functionNo ? `Rom ${context.room.functionNo}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  if (pathOf(row) === "article.id" && typeof value === "object" && !Array.isArray(value)) {
    const article = value as { id?: unknown; compCode?: unknown; name?: unknown };
    return (
      [article.compCode, article.name, article.id == null ? null : `ID ${article.id}`]
        .filter((item) => item != null && item !== "")
        .join(" · ") || "—"
    );
  }
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (Array.isArray(value))
    return value
      .map((item) => (typeof item === "object" ? JSON.stringify(item) : String(item)))
      .join("\n");
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}
